import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/home/Footer';
import useAuthStore, { isTokenExpired } from '../../stores/authStore';
import { getMyJobPostings } from './postJobApi';
import './PostJobWizard.css';

const STATUS_COPY = {
  pending: {
    eyebrow: 'Pending Review',
    title: 'Pending Review',
    body:
      'Your job posting has been submitted successfully and is currently under review. It typically takes 24–48 hours for approval. You will be notified once your job is approved and published.',
  },
  approved: {
    eyebrow: 'Approved',
    title: 'Your job has been approved!',
    body:
      'Your job posting is now live on CofounderHubs. Our team will find suitable candidates and share them with you.',
  },
  rejected: {
    eyebrow: 'Action required',
    title: 'Job Posting Requires Changes',
    body:
      'We reviewed your job posting and additional changes are required before it can be published. Please review the feedback and update your posting.',
  },
  draft: {
    eyebrow: 'Draft',
    title: 'Draft saved',
    body: 'This posting is still a draft. Continue editing to submit it for review.',
  },
};

function PostJobStatus() {
  const { token, isAuthenticated, user } = useAuthStore();
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!isAuthenticated() || !token || isTokenExpired(token)) {
      setError('Please sign in with your employer account to view posting status.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await getMyJobPostings(token);
      setPostings(data.jobPostings || []);
    } catch (err) {
      setError(err.message || 'Failed to load job postings');
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="post-job-page">
      <div className="post-job-container">
        <div className="post-job-hero">
          <p className="post-job-eyebrow">Employer dashboard</p>
          <h1>Your job postings</h1>
          <p>
            Track review status for each listing. You will also get an in-app notification when an
            admin approves or requests changes.
          </p>
        </div>

        {user?.email && (
          <p className="post-job-eyebrow" style={{ marginBottom: '1rem' }}>
            Signed in as {user.email}
          </p>
        )}

        {loading && <div className="post-job-card">Loading your postings...</div>}
        {error && !loading && <div className="post-job-error">{error}</div>}

        {!loading && !error && postings.length === 0 && (
          <div className="post-job-card">
            <h2>No postings yet</h2>
            <p>Submit a job to see its review status here.</p>
            <div className="post-job-actions">
              <Link to="/post-job" className="post-job-btn primary">
                Post a job
              </Link>
            </div>
          </div>
        )}

        {!loading &&
          postings.map((posting) => {
            const key =
              posting.approvalStatus ||
              (posting.paymentStatus === 'draft' || !posting.isPublished ? 'draft' : 'pending');
            const copy = STATUS_COPY[key] || STATUS_COPY.pending;

            return (
              <div key={posting._id} className="post-job-card" style={{ marginBottom: '1rem' }}>
                <p className="post-job-eyebrow">{copy.eyebrow}</p>
                <h2 style={{ marginTop: 0 }}>{copy.title}</h2>
                <p>
                  <strong>{posting.title}</strong>
                  {posting.companyId?.name ? ` · ${posting.companyId.name}` : ''}
                </p>
                <p>{copy.body}</p>
                {key === 'rejected' && posting.adminFeedback && (
                  <p>
                    <strong>Feedback:</strong> {posting.adminFeedback}
                  </p>
                )}
                <div className="admin-muted" style={{ marginTop: '0.75rem', color: '#6b7280' }}>
                  Plan: {posting.plan || '—'} · Pay: {posting.paymentStatus || '—'} · Updated{' '}
                  {posting.updatedAt ? new Date(posting.updatedAt).toLocaleString() : '—'}
                </div>
                <div className="post-job-actions" style={{ marginTop: '1rem' }}>
                  {key === 'approved' && posting.publishedJobId && (
                    <Link to={`/careers/${posting.publishedJobId}`} className="post-job-btn primary">
                      View live listing
                    </Link>
                  )}
                  {(key === 'rejected' || key === 'draft') && (
                    <Link to="/post-job?step=2" className="post-job-btn primary">
                      Update posting
                    </Link>
                  )}
                  <Link to="/notifications" className="post-job-btn secondary">
                    Notifications
                  </Link>
                </div>
              </div>
            );
          })}
      </div>
      <Footer />
    </div>
  );
}

export default PostJobStatus;
